import React, { useState, useMemo, useEffect } from "react";
import {  Input, Button, Modal, ModalHeader, ModalBody, ModalFooter, Col, Row } from "reactstrap";
import 'react-calendar/dist/Calendar.css';
import { formatDateDDMMM, formatDateYYYYMMDD } from "../../../helpers/utils";
import {  useQueryRetrieveNutrition } from "../../../services/stores/queries";
import { NutritionEntity } from "../../../types/nutrition";
import { useMutationExecuteDayFertigation } from "../../../services/stores/queries";
import { toast, Toaster } from "react-hot-toast";
import { ProductCard } from "../../../components/ProductCard";
import { ApplicationsCalendar, combineDateClassNames } from "../../../components/ApplicationsCalendar";
import {  Check, Lock, Unlock } from 'react-feather';
import { SprayExecutedProduct, SprayProduct } from "../../../types/spray";
import Calendar from 'react-calendar';
import { Calendar as CalendarIcon } from 'react-feather';
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../services/stores/queries";
import { useProducts, useUnits } from "../../../helpers/hooks";

type CalendarValue = Date | Date[] | null | [Date | null, Date | null];

export const NutritionPlanned = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showConfirmCalendar, setShowConfirmCalendar] = useState(false);
  const { data: nutritionProgram, isLoading } = useQueryRetrieveNutrition();
  const { products } = useProducts();
  const { units } = useUnits();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [completionDate, setCompletionDate] = useState<Date>(selectedDate);
  const [pendingCompletionDate, setPendingCompletionDate] = useState<Date>(selectedDate);
  const executeDayFertigationMutation  = useMutationExecuteDayFertigation();
  const [showingPlannedApplications, setShowingPlannedApplications] = useState(false);
  const [dateClassNames, setDateClassNames] = useState<Record<string, string[]>>({});
  const [selectedApplications, setSelectedApplications] = useState<NutritionEntity[]>([]);
  const [canEdit, setCanEdit] = useState(false);
  const [cancelledConfirmation, setCancelledConfirmation] = useState(false);

  const queryClient = useQueryClient();

   const toggleConfirmCalendar = () => {
    setPendingCompletionDate(completionDate);
    setShowConfirmCalendar(!showConfirmCalendar);
  };

  const toProductName = (productID?: string) =>
    products?.find(p => p.productID === productID)?.productName || '';

  const toUnitName = (unitID?: string) =>
    units?.find(u => u.unitID === unitID)?.unitShortName || '';

  useEffect(() => {

      const selectedPlannedApplications = nutritionProgram?.applicationsPlannedGroups.find(
        group => new Date(group.applications?.[0]?.plannedDate).toDateString() === selectedDate.toDateString()
      )?.applications || [];

    const selectedExecutedApplications = (nutritionProgram?.applicationsExecutedGroups || []).flatMap(
      group => (group.applications || []).filter(
        app => new Date(app.executedDate).toDateString() === selectedDate.toDateString()
      )
    );



    setSelectedApplications(selectedPlannedApplications.concat(selectedExecutedApplications));
    
  const plannedDateClassNames = (nutritionProgram?.applicationsPlannedGroups || []).reduce((acc, group) => {

     group.applications.forEach(app => {
      const dateStr = formatDateYYYYMMDD(app.plannedDate) || '';
    if (!acc[dateStr]) {
          acc[dateStr] = [];
      }
      if (!acc[dateStr].includes('has-planned-applications')) {  
        acc[dateStr].push('has-planned-applications');
      }
     });
    return acc;
    }, {} as Record<string, string[]>);
 
  
  const executedDateClassNames = (nutritionProgram?.applicationsExecutedGroups || []).reduce((acc, group) => {
  
     group.applications.forEach(app => {
      const dateStr = formatDateYYYYMMDD(app.executedDate) || '';
        if (!acc[dateStr]) {
            acc[dateStr] = [];
        }
        if (!acc[dateStr].includes('has-executed-applications')) {  
          acc[dateStr].push('has-executed-applications');
        }
    });
    return acc;
  }, {} as Record<string, string[]>);

    setDateClassNames(combineDateClassNames(plannedDateClassNames, executedDateClassNames));

},[nutritionProgram, selectedDate]);

  useEffect(() => {

    setCanEdit(selectedApplications.length > 0 && (selectedApplications.some(application => application.executedDate === null 
                                                      || application.executedDate === undefined 
                                                      || application.executedDate === ""  )));
  },[selectedApplications]);

  const handleDateChange = (value: CalendarValue) => {
    if (value instanceof Date) {
      setSelectedDate(value);
    }
  };


  useEffect(() => {
    setCompletionDate(selectedDate);    

    if (cancelledConfirmation) {
      setCancelledConfirmation(false);
      queryClient.invalidateQueries({queryKey: [QUERY_KEYS.NUTRITION]});
      queryClient.invalidateQueries({queryKey: [QUERY_KEYS.INVENTORY]});
    }

  }, [selectedDate]);

  const handleApplicationDateChange = (value: CalendarValue) => {
    if (value instanceof Date) {
      setPendingCompletionDate(value);
    }
  };

  const onConfirmCalendarDate = () => {
    setCompletionDate(pendingCompletionDate);
    selectedApplications.forEach(application => {
      application.executedDate = pendingCompletionDate.toDateString();
    });
    setShowConfirmCalendar(false);
  };

  const onCloseCalendar = () => {
    setShowConfirmCalendar(false);
  };

  const handleProductEdit = (product: NutritionEntity | SprayProduct | SprayExecutedProduct, editAmount: number | undefined) => {
    const nutrition = selectedApplications.find(application => application.productID === product.productID);
    if (nutrition && editAmount !== undefined) {
      nutrition.actualQuantity = editAmount;
      nutrition.quantity = editAmount;
    }
  };


  const onCancelConfirmation = () => {
    setShowConfirmCalendar(false);
    setShowConfirmModal(false);
    setCancelledConfirmation(true);


  };

  const onConfirmNutritionComplete = async () => {
    try {

      selectedApplications.forEach(application => {
        if (application.actualQuantity === null || 
          application.actualQuantity === undefined) {
          application.actualQuantity = application.quantity;
        }
        application.executedDate = formatDateYYYYMMDD(completionDate)+'';
      });

      executeDayFertigationMutation.mutate(selectedApplications, 
        {
          onSuccess: (data) => {
            setShowConfirmCalendar(false);
            setShowConfirmModal(false);
            toast.success('La fertigation a été enregistrée avec succès');
          },
          onError: (error) => {
              toast.error(error.message);
          }
      })

    } catch (error) {
      console.error('Error completing nutrition:', error);
    }
  };

  const promptUserForConfirmation = () => {
    selectedApplications.forEach(application => {
      if (application.actualQuantity === null || 
        application.actualQuantity === undefined) {
        application.actualQuantity = application.quantity;
      }
    });
    setShowConfirmModal(true);
  };
  
  useEffect(() => {
    setShowingPlannedApplications((selectedApplications.length > 0) && (selectedApplications[0].executedDate === null));
  },[selectedApplications, selectedApplications[0]?.executedDate]);


  if (isLoading) {
    return <div className="d-flex justify-content-center"><div className="spinner-border text-primary" /></div>;
  }


    return (
      <div className="nutrition-planned">

      <Toaster />

      <div style={{ marginBottom: '10px' }} >
        <ApplicationsCalendar
          dateClassNames={dateClassNames}
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
        />
      </div>

      {selectedApplications.length > 0 ? (
        <div className="product-cards">
          {selectedApplications.map((nutritionProduct: NutritionEntity, indexProduct: number) => (
              <div key={ indexProduct}>
              <ProductCard 
                key={ indexProduct} 
                product={nutritionProduct} 
                isRealized={nutritionProduct.executedDate !== null}
                canEdit={canEdit}
                onEdit={handleProductEdit}
                message={(nutritionProduct.executedDate !== null) &&  (nutritionProduct.plannedDate !== nutritionProduct.executedDate )? "Fertigation réalisée le " + nutritionProduct.executedDate : undefined}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center text-muted mt-4">
          <h4>Aucune fertigation prévue pour le {formatDateDDMMM(selectedDate)}</h4>
        </div>
      )}

      {canEdit &&  (selectedApplications.length > 0) &&
        <div className="text-center mt-4">
            <Button
              color="success"
              onClick={promptUserForConfirmation}
              className="d-flex align-items-center mx-auto"
            >
              <Check size={18} className="me-2" />
              {showingPlannedApplications?
              "Confirmer la fértigation"
              :
              "Mettre a jour les quantités realisées"
              }
            </Button>
        </div>
      }

      {!showingPlannedApplications && (selectedApplications.length > 0) &&
        <div 
          className="nutrition-executer-banner d-flex align-items-center" 
          onClick={() => setCanEdit(!canEdit)}
          style={{ cursor: 'pointer' }}
        >
          {canEdit ? (
            <Unlock size={18} className="me-2" />
          ) : (
            <Lock size={18} className="me-2" />
          )}
          Fertigation déjà réalisée.
        </div>
      }

<Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(false)}>
            <ModalHeader toggle={() => setShowConfirmModal(false)}>
              Confirmer la réalisation de la nutrition
            </ModalHeader>
            <ModalBody>
              <div className="mb-4">
                <label className="form-label">Date de réalisation </label>
                <Row   onClick={toggleConfirmCalendar}                >
                  <Col xs={8}>
                      <h3 
                      className="text-primary clickable-date"
                      style={{ cursor: 'pointer', fontSize: '1.2rem', fontWeight: 'bold', marginLeft: '10px' }}
                    >
                      {formatDateYYYYMMDD(completionDate)}
                    </h3>
                  </Col>
                   <Col xs={4}>
                    <CalendarIcon  />
                  </Col>
                </Row>
              </div>

              {showConfirmCalendar && (
                  <div className="calendar-wrapper">
                    <Calendar
                      onChange={handleApplicationDateChange}
                      value={pendingCompletionDate}
                      className="rounded shadow-sm"
                      formatShortWeekday={(locale, date) => {
                        const day = date.getDay();
                        const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
                        return weekDays[day];
                      }}
                    />
                    <div className="calendar-actions">
                      <Button color="secondary" outline onClick={onCloseCalendar} className="calendar-action-button">
                        Fermer
                      </Button>
                      <Button color="primary" onClick={onConfirmCalendarDate} className="calendar-action-button">
                        OK
                      </Button>
                    </div>
                  </div>
                )}             
              <div className="applications-list">
                {selectedApplications.map((nutrition, index) => (
                  <div key={index} className="mb-3 p-3 border rounded">
                    <h6>{toProductName(nutrition.productID)}</h6>
                    <div className="d-flex justify-content-between">
                      <span>Programmé: {nutrition.quantity} {toUnitName(nutrition.unitID)}</span>
                      <span>Réalisé: {nutrition.actualQuantity || '0'} {toUnitName(nutrition.unitID)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" onClick={() => onCancelConfirmation()}>
                Annuler
              </Button>
              <Button color="success" onClick={onConfirmNutritionComplete}>
                Confirmer
              </Button>
            </ModalFooter>
          </Modal>


    </div>
  );
};
